import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import Helper from "../helpers";
import config from "../config";
import SessionView from "../components/SessionView";
import LayoutWrapper from "../components/LayoutWrapper";

const baseUrl = config.apiBaseUrl;

const SessionList = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const selectedQuery = queryParams.get('selected');

    const [sessionData, setSessionData] = useState([]);
    const [groupedSessionData, setGroupedSessionData] = useState({});
    const [usersList, setUsersList] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [value, setValue] = useState(selectedQuery === 'mysessions' ? 'mySessions' : 'allDates');
    // const [currentVenueKey, setCurrentVenueKey] = useState("");

    const getDisplayedSessions = () => {
        if (value === 'mySessions') {
            return sessionData.filter(session => usersList.includes(session.sessionId));
        }
        
        if (selectedDate) {
            return groupedSessionData[selectedDate] || [];
        }
        
        return [];
    };

    const displayedSessions = getDisplayedSessions();

    useEffect(() => {
        const fetchUserSessions = async (cvk) => {
            try {
                const token = await Helper.getCookie('wh-jwt');
                const response = await fetch(`${baseUrl}/api/sessions/user/${cvk}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include'
                });
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                const userSessionIds = data.userSessions.map(session => session.bookableSessionId);
                setUsersList(userSessionIds);
                const formattedEvents = data.availableSessions.map(session => {
                    const start = new Date(session.startDate);
                    return {
                        sessionId: session.id,
                        title: session.name,
                        startDate: session.startDate,
                        start,
                        end: new Date(start.getTime() + session.numberOfMinuets * 60000),
                        sessionBookText: userSessionIds.includes(session.id) ? 'Booked' : 'Book',
                        availableSeats: session.availableSeats,
                        seatsTaken: session.seatsTaken,
                        numberOfMinuets: session.numberOfMinuets,
                        sessionCost: session.tokenCost
                    };
                });

                setSessionData(formattedEvents);

                // Group sessions by date
                const groupedSessions = formattedEvents
                    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
                    .reduce((acc, session) => {
                        const date = session.startDate.split('T')[0];
                        if (!acc[date]) {
                            acc[date] = [];
                        }
                        acc[date].push(session);
                        return acc;
                    }, {});
                
                setGroupedSessionData(groupedSessions);
                
                // Set initial selected date to first available date
                const dates = Object.keys(groupedSessions);
                if (dates.length > 0 && selectedQuery !== 'mysessions') {
                    setSelectedDate(dates[0]);
                }
            } catch (error) {
                console.error('Error fetching user sessions', error);
            }
        }

        const initialize = async () => {
            let venue = await Helper.getUserVenueId();
            //setCurrentVenueKey(venue);
            await fetchUserSessions(venue);
        };

        initialize();
    }, [selectedQuery]);

    if (!sessionData || sessionData.length <= 0) {
        return (
            <LayoutWrapper>
                <div className="min-h-screen text-white">
                    <div className="container mx-auto py-8 sm:px-1 lg:px-4">
                        <div className="card-luxury p-8 text-center">
                            <CalendarIcon className="h-16 w-16 text-emerald-600/50 mx-auto mb-4" />
                            <h3 className="text-xl font-display font-semibold text-accent-platinum mb-2">No Sessions Available</h3>
                            <p className="text-emerald-600/60 font-body">Check back later for new session availability.</p>
                        </div>
                    </div>
                </div>
            </LayoutWrapper>
        );
    }

    return (
        <LayoutWrapper>
            <div className="min-h-screen  text-white">
                <div className="container mx-auto py-8 sm:px-1 lg:px-4">
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-4xl font-display font-bold text-gradient-emerald mb-2">Available Sessions</h1>
                        <p className="text-accent-platinum/70 font-body">Book your training sessions</p>
                    </div>

                    {/* Filter Tabs */}
                    <div className="mb-8">
                        <div className="flex flex-wrap gap-2 sm:gap-4 border-b border-emerald-600/20">
                            <button
                                onClick={() => {
                                    setValue('allDates');
                                    setSelectedDate(Object.keys(groupedSessionData)[0]);
                                }}
                                className={`px-6 py-3 text-sm sm:text-base font-display font-semibold transition-all duration-300 border-b-2 tracking-wide ${
                                    value === 'allDates'
                                        ? 'border-emerald-500 text-emerald-300'
                                        : 'border-transparent text-accent-platinum/60 hover:text-emerald-300 hover:border-emerald-600/50'
                                }`}
                            >
                                All Sessions
                            </button>
                            <button
                                onClick={() => {
                                    setValue('mySessions');
                                    setSelectedDate(null);
                                }}
                                className={`px-6 py-3 sm:px-1 text-sm sm:text-base font-display font-semibold transition-all duration-300 border-b-2 tracking-wide ${
                                    value === 'mySessions'
                                        ? 'border-emerald-500 text-emerald-300'
                                        : 'border-transparent text-accent-platinum/60 hover:text-emerald-300 hover:border-emerald-600/50'
                                }`}
                            >
                                My Sessions
                            </button>
                        </div>
                    </div>

                    {value === 'allDates' && (
                        <>
                            {/* Date Cards */}
                            <div className="mb-8">
                                <div className="card-luxury p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <CalendarIcon className="h-5 w-5 text-emerald-400" />
                                        <span className="text-emerald-300 font-display font-semibold">Select a Date:</span>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <div className="flex gap-3 pb-2 min-w-max">
                                            {Object.keys(groupedSessionData).map(date => {
                                                const sessionCount = groupedSessionData[date].length;
                                                const bookedCount = groupedSessionData[date].filter(session => 
                                                    usersList.includes(session.sessionId)
                                                ).length;
                                                
                                                return (
                                                    <button
                                                        key={date}
                                                        onClick={() => setSelectedDate(date)}
                                                        className={`flex-shrink-0 p-4 rounded-xl border-2 transition-all duration-300 text-center min-w-[100px] font-display ${
                                                            selectedDate === date
                                                                ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300 shadow-luxury'
                                                                : 'border-emerald-600/50 bg-luxury-gradient text-accent-platinum hover:border-emerald-500/50 hover:bg-emerald-600/5'
                                                        }`}
                                                    >
                                                        <div className="text-sm font-medium mb-1">
                                                            {format(parseISO(date), 'EEE')}
                                                        </div>
                                                        <div className="text-2xl font-bold mb-1">
                                                            {format(parseISO(date), 'dd')}
                                                        </div>
                                                        <div className="text-xs text-emerald-300/95">
                                                            {format(parseISO(date), 'MMM')}
                                                        </div>
                                                        <div className="mt-2 text-xs font-body">
                                                            <div className="text-emerald-400/90">{sessionCount} sessions</div>
                                                            {bookedCount > 0 && (
                                                                <div className="text-emerald-300 font-semibold">{bookedCount} booked</div>
                                                            )}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Selected Date Header */}
                            {selectedDate && (
                                <div className="mb-6">
                                    <h2 className="text-2xl font-display font-bold text-gradient-emerald mb-2">
                                        Sessions for {format(parseISO(selectedDate), 'EEEE, MMMM dd, yyyy')}
                                    </h2>
                                    <p className="text-gray-400">
                                        {displayedSessions.length} session{displayedSessions.length !== 1 ? 's' : ''} available
                                    </p>
                                </div>
                            )}
                        </>
                    )}

                    {value === 'mySessions' && (
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-gray-100 mb-2">My Booked Sessions</h2>
                            <p className="text-gray-400">
                                {displayedSessions.length} session{displayedSessions.length !== 1 ? 's' : ''} booked
                            </p>
                        </div>
                    )}

                    {/* Sessions List - Vertical Stack */}
                    <div className="space-y-4">
                        {displayedSessions.length > 0 ? (
                            displayedSessions.map((session) => (
                                <SessionView key={session.sessionId} session={session} />
                            ))
                        ) : (
                            <div className="bg-gray-900 border border-gray-700 rounded-lg p-8 text-center">
                                <CalendarIcon className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                                <h3 className="text-xl font-medium text-gray-300 mb-2">No Sessions Found</h3>
                                <p className="text-gray-500">
                                    {value === 'mySessions' 
                                        ? "You haven't booked any sessions yet." 
                                        : selectedDate 
                                            ? "No sessions scheduled for this date."
                                            : "Select a date to view sessions."
                                    }
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </LayoutWrapper>
    );
};

export default SessionList;
